// pages/strategic-playbook/collections/[slug].js
// ════════════════════════════════════════════════════════════════════
// Collection detail — renders one curated reading path as an ordered
// list of entries with editorial notes between them. Static-rendered
// from the COLLECTIONS data file at build time.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { COLLECTIONS, getCollection } from '../../../data/strategic-playbook/collections';
import { listFindings } from '../../../features/findings/registry';
import { listTectonicShifts } from '../../../data/strategic-playbook/tectonic-shifts';
import { DOSSIERS } from '../../../data/strategic-playbook/dossiers';
import { listBriefs } from '../../../data/lab/briefs';

export async function getStaticPaths() {
  return {
    paths: COLLECTIONS.map((c) => ({ params: { slug: c.slug } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const collection = getCollection(params.slug);
  if (!collection) return { notFound: true };

  const findingsByHandle = {};
  for (const f of listFindings()) findingsByHandle[f.citationHandle] = f;
  const shiftsBySlug = {};
  for (const s of listTectonicShifts()) shiftsBySlug[s.slug] = s;
  const dossiersById = {};
  for (const d of DOSSIERS) dossiersById[d.id] = d;
  const briefsBySlug = {};
  for (const b of listBriefs()) briefsBySlug[b.slug] = b;

  const resolved = collection.entries.map((entry) => {
    if (entry.kind === 'finding') {
      const f = findingsByHandle[entry.ref];
      if (!f) return { ...entry, missing: true };
      return {
        ...entry,
        title: f.title,
        eyebrow: `Lab Finding · Form ${f.form} — ${f.formName}`,
        href: f.path,
        summary: f.description,
        meta: { lifecycle: f.lifecycle, sampleSize: f.sampleSize, domain: f.domain },
      };
    }
    if (entry.kind === 'shift') {
      const s = shiftsBySlug[entry.ref];
      if (!s) return { ...entry, missing: true };
      return {
        ...entry,
        title: s.name,
        eyebrow: s.category === 'historical' ? `Historical shift · ${s.era || ''}` : `Tectonic shift · ${s.era || ''}`,
        href: `/strategic-playbook/tectonic/${s.slug}`,
        summary: s.thesis ? s.thesis.slice(0, 240) + (s.thesis.length > 240 ? '…' : '') : s.note,
        meta: { stage: s.stageLabel, era: s.era },
      };
    }
    if (entry.kind === 'dossier') {
      const d = dossiersById[entry.ref];
      if (!d) return { ...entry, missing: true };
      return {
        ...entry,
        title: d.title,
        eyebrow: d.eyebrow || 'Dossier',
        href: `/strategic-playbook/${d.id}`,
        summary: d.summary,
        meta: {},
      };
    }
    if (entry.kind === 'brief') {
      const b = briefsBySlug[entry.ref];
      if (!b) return { ...entry, missing: true };
      return {
        ...entry,
        title: b.title,
        eyebrow: `Weekly Brief · ${b.weekOf || b.publishDate}`,
        href: `/lab/briefs/${b.slug}`,
        summary: b.description,
        meta: {},
      };
    }
    return { ...entry, missing: true };
  });

  return { props: { collection, resolved } };
}

export default function CollectionDetailPage({ collection, resolved }) {
  return (
    <div className="sp-root">
      <Head>
        <title>{collection.name} · Curated Path · Shift</title>
        <meta name="description" content={collection.description} />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/strategic-playbook/library" className="finding-back">← Library</Link>
          <div className="findings-index-topbar-meta">
            Strategic Playbook · Curated Path
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">{collection.eyebrow}</div>
          <h1 className="findings-index-title">{collection.name}</h1>
          {collection.description && (
            <p className="findings-index-sub">{collection.description}</p>
          )}
        </header>

        {collection.intro && (
          <section className="collection-intro">
            <p>{collection.intro}</p>
          </section>
        )}

        <section className="collection-list">
          {resolved.map((entry, i) => (
            <CollectionEntry key={i} num={i + 1} entry={entry} />
          ))}
        </section>

        <footer className="findings-index-footer">
          <p>
            Curated paths are reading orders, not original artifacts.
            Each entry links to its canonical detail page; the path adds
            sequence + connective tissue. See all paths at <Link href="/strategic-playbook/library" className="methodology-inline-link">/strategic-playbook/library</Link>.
          </p>
        </footer>
      </div>
    </div>
  );
}

function CollectionEntry({ num, entry }) {
  if (entry.missing) {
    return (
      <div className="collection-entry collection-entry-missing">
        <div className="collection-entry-num">{num.toString().padStart(2, '0')}</div>
        <div className="collection-entry-body">
          <div className="collection-entry-eyebrow">Missing</div>
          <div className="collection-entry-title">
            {entry.kind} · <code>{entry.ref}</code>
          </div>
          <p className="collection-entry-note">
            This reference doesn&rsquo;t resolve to a current corpus entry.
            The collection may be ahead of authored content, or the ref
            may have been retracted.
          </p>
        </div>
      </div>
    );
  }
  return (
    <Link href={entry.href} className="collection-entry">
      <div className="collection-entry-num">{num.toString().padStart(2, '0')}</div>
      <div className="collection-entry-body">
        <div className="collection-entry-eyebrow">{entry.eyebrow}</div>
        <h2 className="collection-entry-title">{entry.title}</h2>
        {entry.summary && (
          <p className="collection-entry-summary">{entry.summary}</p>
        )}
        {entry.note && (
          <p className="collection-entry-note">
            <span className="collection-entry-note-label">Why this here:</span> {entry.note}
          </p>
        )}
      </div>
      <div className="collection-entry-arrow">→</div>
    </Link>
  );
}
